/**
 * API Functions for Line Chat History
 * จัดการ API calls สำหรับดูประวัติ Chat จาก Line OA Webhook
 */

import { multiFranchiseAxios } from '../axios/multiFranchiseAxios';
import {
  ILineChatQueryParams,
  ILineChatMessagesQueryParams,
  ILineChatBroadcastHistoryParams,
  ILineChatImagesQueryParams,
  ILineChatSearchParams,
  ILineChatStatsParams,
  ILineChatAttentionRequiredParams,
  TLineChatConversationsResponse,
  TLineChatMessagesResponse,
  TLineChatBroadcastHistoryResponse,
  TLineChatAttentionRequiredResponse,
  TLineChatImagesResponse,
  TLineChatSearchResponse,
  TLineChatStatsResponse,
} from '../../compiler/lineChat.interface';

// ========== BASE PATH ==========
const BASE_PATH = '/procurement/admin/line-chat';

// ========== HELPER FUNCTIONS ==========

/**
 * Build query string from params object
 */
const buildQueryParams = (params?: Record<string, any>): string => {
  if (!params) return '';
  
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ========== API FUNCTIONS ==========

/**
 * 1. ดู Inbox (Conversations List)
 * GET /procurement/admin/line-chat/conversations
 */
export const APIGetLineChatConversations = async (
  params?: ILineChatQueryParams
): Promise<TLineChatConversationsResponse> => {
  const queryString = buildQueryParams(params);
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/conversations${queryString}`);
  return response.data;
};

/**
 * 2. ดูประวัติ chat ของห้องสนทนา
 * - branch conversation: GET /procurement/admin/line-chat/branch/:branchUuid
 * - unassigned conversation: GET /procurement/admin/line-chat/user/:lineUserId
 */
export const APIGetLineChatMessages = async (
  params: ILineChatMessagesQueryParams
): Promise<TLineChatMessagesResponse> => {
  const { branch_uuid, line_user_id, ...queryParams } = params;
  const queryString = buildQueryParams(queryParams);

  if (branch_uuid) {
    const response = await multiFranchiseAxios.get(`${BASE_PATH}/branch/${branch_uuid}${queryString}`);
    return response.data;
  }

  if (line_user_id) {
    const response = await multiFranchiseAxios.get(`${BASE_PATH}/user/${line_user_id}${queryString}`);
    return response.data;
  }

  throw new Error('Either branch_uuid or line_user_id is required');
};

/**
 * 2.1 Mark branch conversation as read (inbound reviewed)
 * POST /procurement/admin/line-chat/branch/:branchUuid/mark-read
 */
export const APIMarkLineChatBranchAsRead = async (
  branchUuid: string
): Promise<{ status: boolean; data: { branch_uuid: string; marked_count: number } }> => {
  if (!branchUuid) {
    throw new Error('branchUuid is required');
  }

  const response = await multiFranchiseAxios.post(`${BASE_PATH}/branch/${branchUuid}/mark-read`);
  return response.data;
};

/**
 * 2.2 Mark unassigned LINE user conversation as read
 * POST /procurement/admin/line-chat/user/:lineUserId/mark-read
 */
export const APIMarkLineChatUserAsRead = async (
  lineUserId: string
): Promise<{ status: boolean; data: { line_user_id: string; marked_count: number } }> => {
  if (!lineUserId) {
    throw new Error('lineUserId is required');
  }

  const response = await multiFranchiseAxios.post(`${BASE_PATH}/user/${lineUserId}/mark-read`);
  return response.data;
};

/**
 * 2.1 ดูประวัติ outbound จาก Broadcast Campaign
 * GET /procurement/admin/line-chat/broadcast-history
 */
export const APIGetLineChatBroadcastHistory = async (
  params?: ILineChatBroadcastHistoryParams
): Promise<TLineChatBroadcastHistoryResponse> => {
  const queryString = buildQueryParams(params);
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/broadcast-history${queryString}`);
  return response.data;
};

/**
 * 2.2 ดูข้อความที่ต้องการ admin attention
 * GET /procurement/admin/line-chat/attention-required
 */
export const APIGetLineChatAttentionRequired = async (
  params?: ILineChatAttentionRequiredParams
): Promise<TLineChatAttentionRequiredResponse> => {
  const queryString = buildQueryParams(params);
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/attention-required${queryString}`);
  return response.data;
};

/**
 * 3. ดูรูปภาพทั้งหมด
 * GET /procurement/admin/line-chat/images
 */
export const APIGetLineChatImages = async (
  params?: ILineChatImagesQueryParams
): Promise<TLineChatImagesResponse> => {
  const queryString = buildQueryParams({
    ...params,
    has_media_url: params?.has_media_url ?? true,
  });
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/images${queryString}`);
  return response.data;
};

/**
 * 4. ค้นหาข้อความ
 * GET /procurement/admin/line-chat/search
 */
export const APISearchLineChatMessages = async (
  params: ILineChatSearchParams
): Promise<TLineChatSearchResponse> => {
  if (!params.q) {
    throw new Error('Search query (q) is required');
  }
  
  const queryString = buildQueryParams(params);
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/search${queryString}`);
  return response.data;
};

/**
 * 5. ดูสถิติ chat รายวัน
 * GET /procurement/admin/line-chat/stats/daily-volume
 */
export const APIGetLineChatStats = async (
  params?: ILineChatStatsParams
): Promise<TLineChatStatsResponse> => {
  const queryString = buildQueryParams(params);
  const response = await multiFranchiseAxios.get(`${BASE_PATH}/stats/daily-volume${queryString}`);
  return response.data;
};

// ========== EXPORT ALL ==========
export default {
  getConversations: APIGetLineChatConversations,
  getMessages: APIGetLineChatMessages,
  markBranchAsRead: APIMarkLineChatBranchAsRead,
  markUserAsRead: APIMarkLineChatUserAsRead,
  getBroadcastHistory: APIGetLineChatBroadcastHistory,
  getAttentionRequired: APIGetLineChatAttentionRequired,
  getImages: APIGetLineChatImages,
  search: APISearchLineChatMessages,
  getStats: APIGetLineChatStats,
};
